import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Extract session_id from URL fragment
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        setError('No session ID found. Please try again.');
        return;
      }

      const sessionId = sessionIdMatch[1];

      // Retry logic for transient failures
      const maxRetries = 3;
      let lastError = null;

      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          const response = await fetch(`${API}/auth/session`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ session_id: sessionId }),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || 'Authentication failed');
          }

          const data = await response.json();
          
          // Store the session token in multiple places for reliability
          if (data.session_token) {
            try {
              localStorage.setItem('session_token', data.session_token);
              sessionStorage.setItem('session_token', data.session_token);
            } catch (storageError) {
              // Continue anyway - cookie auth should work
              console.warn('Storage error:', storageError);
            }
          }
          
          // Login and navigate
          login(data.user, data.session_token);
          toast.success(`Welcome, ${data.user.name}!`);
          
          // Small delay then navigate
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Check for redirect URL stored before login (check both storages)
          let redirectUrl = sessionStorage.getItem('redirectAfterLogin') || localStorage.getItem('redirectAfterLogin');
          
          if (redirectUrl) {
            // Clear from both storages
            sessionStorage.removeItem('redirectAfterLogin');
            localStorage.removeItem('redirectAfterLogin');
            navigate(redirectUrl, { replace: true });
          } else {
            navigate('/', { replace: true });
          }
          return; // Success
          
        } catch (err) {
          lastError = err;
          console.error(`Auth attempt ${attempt + 1} failed:`, err);
          
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 1500));
            setRetryCount(attempt + 1);
          }
        }
      }

      // All retries failed
      console.error('All auth attempts failed:', lastError);
      setError('Login failed. Please try again.');
    };

    processAuth();
  }, [location, navigate, login]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <p className="text-destructive text-lg mb-2 font-medium">{error}</p>
          <p className="text-muted-foreground text-sm mb-4">
            Please try logging in again.
          </p>
          <button 
            onClick={() => navigate('/login')}
            className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
        <p className="text-lg font-heading text-primary">Authenticating...</p>
        <p className="text-muted-foreground mt-2">
          {retryCount > 0 ? `Retrying... (attempt ${retryCount + 1})` : 'Please wait'}
        </p>
      </div>
    </div>
  );
};

export default AuthCallback;
